import React, { useState, useCallback } from 'react';
import { X, Upload, CheckCircle, FileImage } from 'lucide-react';
import Files from 'react-files';
import { uploadLabDocs, uploadGalleryDocs } from '../../apiCalls/user';

const DOC_LABELS = {
  'main-img':               'Main Photo',
  'medical-license':        'Medical License',
  'insurance-certificate':  'Insurance Certificate',
  'reg-certificate':        'Registration Certificate',
  'reception-area':         'Reception Area',
  'testing-lab':            'Testing Lab',
  'equipment-area':         'Equipment Area',
  'waiting-area':           'Waiting Area',
  'exterior-area':          'Exterior View',
};

const UploadModal = ({ show, labId, uploadType, isGallery, onClose, onSuccess }) => {
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');

  const handleFileChange = useCallback((files) => {
    if (!files?.length) return;
    const f = files[0];
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError('');
  }, []);

  const handleFileError = (err) => {
    setError(`File error: ${err.message}`);
  };

  const handleSubmit = async () => {
    if (!file) { setError('Please select a file first.'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('documents', file);
    const cb = (res) => {
      setUploading(false);
      setFile(null);
      setPreview(null);
      onSuccess();
    };
    if (isGallery) uploadGalleryDocs(formData, uploadType, labId, cb);
    else           uploadLabDocs(formData, uploadType, labId, cb);
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setError('');
    onClose();
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1050,
      background: 'rgba(15,10,36,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      backdropFilter: 'blur(3px)',
      animation: 'modal-fade-in 0.18s ease',
    }}>
      <style>{`
        @keyframes modal-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
        .upload-dropzone { cursor: pointer; }
        .upload-dropzone:focus { outline: none; }
      `}</style>

      <div style={{
        background: 'var(--white)',
        borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: 460,
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--gray-100)',
          background: 'var(--primary-light)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: 34, height: 34, borderRadius: 'var(--radius-md)',
              background: 'var(--primary-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Upload size={16} style={{ color: 'var(--primary)' }} />
            </div>
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                Upload {DOC_LABELS[uploadType] || 'File'}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>
                JPG, PNG, JPEG, WEBP accepted
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--gray-400)', padding: '0.3rem',
              borderRadius: 'var(--radius)', transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-muted)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--gray-400)'; e.currentTarget.style.background = 'none'; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>

          {/* Drop zone */}
          <Files
            className="upload-dropzone"
            onChange={handleFileChange}
            onError={handleFileError}
            accepts={['.jpg', '.png', '.jpeg', '.webp']}
            maxFileSize={10000000}
            minFileSize={0}
            clickable
          >
            <div style={{
              border: `2px dashed ${preview ? 'var(--primary)' : 'var(--gray-200)'}`,
              borderRadius: 'var(--radius-lg)',
              background: preview ? 'var(--primary-light)' : 'var(--gray-50)',
              padding: '1.5rem',
              textAlign: 'center',
              transition: 'all 0.18s',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 180,
            }}>
              {preview ? (
                <>
                  <img
                    src={preview}
                    alt="preview"
                    style={{
                      maxHeight: 150, maxWidth: '100%',
                      borderRadius: 'var(--radius-md)',
                      objectFit: 'contain',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                    <CheckCircle size={14} style={{ color: 'var(--success)' }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--success)' }}>
                      {file?.name}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 4 }}>
                    Click to change file
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'var(--primary-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1rem',
                  }}>
                    <FileImage size={24} style={{ color: 'var(--primary)' }} />
                  </div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
                    Click to browse
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                    or drag &amp; drop your image here
                  </div>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--gray-300)' }}>
                    Max size: 10 MB
                  </div>
                </>
              )}
            </div>
          </Files>

          {error && (
            <div style={{
              marginTop: '0.75rem', padding: '0.6rem 0.875rem',
              background: 'var(--danger-light)', borderRadius: 'var(--radius-md)',
              fontSize: '0.8rem', color: '#dc2626',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}>
              <i className="bi bi-exclamation-circle" /> {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
            <button
              className="app-btn app-btn--primary"
              style={{ flex: 1 }}
              onClick={handleSubmit}
              disabled={uploading || !file}
            >
              {uploading ? (
                <><span className="svc-spinner" /> Uploading…</>
              ) : (
                <><Upload size={14} /> Upload</>
              )}
            </button>
            <button className="app-btn app-btn--ghost" onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
